import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  X, ZoomIn, ZoomOut, RotateCw, RotateCcw, Star, Maximize2,
  Square, Type, Trash2, CheckCircle, XCircle, HelpCircle, MessageSquare,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

// Aug 2026: HEIC assets aren't renderable by any browser (iPhone default
// upload format). Route them through the backend transcoder endpoint that
// returns JPEG. Non-HEIC assets keep hitting S3 directly (fast path, no
// server CPU spent on transcoding for already-web-safe formats).
const _isHeic = (asset) => {
  const k = (asset?.storage_key || '').toLowerCase();
  const ct = (asset?.content_type || '').toLowerCase();
  return k.endsWith('.heic') || k.endsWith('.heif') || ct.includes('heic') || ct.includes('heif');
};
const _renderUrl = (asset) =>
  `${API_BASE}/team/items/${asset.order_item_id}/assets/${asset.id}/render.jpg`;

const LABEL_TYPES = [
  { value: 'real', label: 'Authentic', color: '#0E6B5D', icon: CheckCircle },
  { value: 'fake', label: 'Counterfeit', color: '#5A1F2A', icon: XCircle },
  { value: 'question', label: 'Needs Review', color: '#A36A3A', icon: HelpCircle },
  { value: 'note', label: 'Note', color: '#1F5E7A', icon: MessageSquare },
];

export default function ImageInspector({ asset, onClose, onRotate, onToggleCert, onPrev, onNext, position }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  // Aug 2026: self-healing retry. On <img> onerror, once, we fetch a fresh
  // signed URL from the backend and try again. Only shows the error UI if the
  // retry also fails. ``diagnosticKey`` surfaces the underlying storage_key
  // so admins can copy it into a support message.
  const [freshUrl, setFreshUrl] = useState(null);
  const [diagnosticKey, setDiagnosticKey] = useState(null);
  const [imgEl, setImgEl] = useState(null);
  const [mode, setMode] = useState('view'); // 'view' or 'annotate'
  const [annotations, setAnnotations] = useState([]);
  const [drawing, setDrawing] = useState(null); // {startX, startY, endX, endY}
  const [annotationLabel, setAnnotationLabel] = useState('');
  const [annotationLabelType, setAnnotationLabelType] = useState('note');
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [pendingRect, setPendingRect] = useState(null);

  const isHeic = _isHeic(asset);
  const baseUrl = isHeic
    ? _renderUrl(asset)
    : (asset.url || (asset.storage_key?.startsWith('/') ? asset.storage_key : `/api/storage/files/${asset.storage_key}`));
  const url = freshUrl || baseUrl;
  const rotation = asset.rotation_degrees || 0;

  // Load annotations
  useEffect(() => {
    api.get(`/admin/annotations/${asset.id}`).then(r => setAnnotations(r.data)).catch(() => {});
  }, [asset.id]);

  // Reset view when navigating between images (carousel)
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [asset.id]);

  // Keyboard: ArrowLeft / ArrowRight move through images, Escape closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onPrev, onNext, onClose]);

  // Load image. No crossOrigin: S3 presigned URLs carry no CORS headers, so a
  // CORS-mode load is blocked by the browser and the viewer never renders.
  // We only draw to canvas (never read pixels back), so a tainted canvas is fine.
  //
  // Self-healing (Aug 2026): if <img>.onerror fires and we haven't retried yet,
  // hit /team/items/<id>/assets/<id>/url for a fresh signed URL and try again.
  // Uses refs so StrictMode double-invocation + rapid asset navigation cannot
  // fire duplicate retries or leak a stale error UI onto a valid image.
  const retryStateRef = useRef({ assetId: null, retried: false, cancelled: false });
  useEffect(() => {
    // Fresh session for each asset: reset retry guard, clear stale UI.
    retryStateRef.current.cancelled = true;
    retryStateRef.current = { assetId: asset.id, retried: false, cancelled: false };
    setImgError(false);
    setImgLoaded(false);
    setDiagnosticKey(null);
    setFreshUrl(null);
    return () => { retryStateRef.current.cancelled = true; };
  }, [asset.id]);

  useEffect(() => {
    const ctx = retryStateRef.current;
    if (ctx.cancelled) return;
    const img = new Image();
    let localCancelled = false;
    img.onload = () => {
      if (localCancelled || ctx.cancelled || ctx.assetId !== asset.id) return;
      setImgEl(img);
      setImgLoaded(true);
      setImgError(false);
    };
    img.onerror = async () => {
      if (localCancelled || ctx.cancelled || ctx.assetId !== asset.id) return;
      if (ctx.retried) {
        setImgError(true);
        setDiagnosticKey(asset.storage_key || '(no storage_key on asset)');
        return;
      }
      ctx.retried = true;
      try {
        const itemId = asset.order_item_id;
        if (!itemId) throw new Error('no order_item_id on asset');
        const r = await api.get(`/team/items/${itemId}/assets/${asset.id}/url`);
        if (ctx.cancelled || ctx.assetId !== asset.id) return;
        setFreshUrl(r.data.url);
      } catch (e) {
        if (ctx.cancelled || ctx.assetId !== asset.id) return;
        setImgError(true);
        setDiagnosticKey(e.response?.data?.detail || asset.storage_key || '(unknown)');
      }
    };
    img.src = url;
    return () => { localCancelled = true; img.onload = null; img.onerror = null; };
  }, [url, asset.id, asset.storage_key, asset.order_item_id]);

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgEl) return;
    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    if (!container) return;

    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // Apply zoom and pan
    ctx.translate(canvas.width / 2 + pan.x, canvas.height / 2 + pan.y);
    ctx.scale(zoom, zoom);
    ctx.rotate((rotation * Math.PI) / 180);

    // Draw image centered
    const scale = Math.min((canvas.width * 0.9) / imgEl.width, (canvas.height * 0.9) / imgEl.height);
    const w = imgEl.width * scale;
    const h = imgEl.height * scale;
    ctx.drawImage(imgEl, -w / 2, -h / 2, w, h);

    ctx.restore();

    // Draw annotations (in screen space)
    annotations.forEach(a => {
      const lt = LABEL_TYPES.find(t => t.value === a.label_type) || LABEL_TYPES[3];
      ctx.strokeStyle = lt.color;
      ctx.lineWidth = 2;
      ctx.setLineDash([]);

      // Convert normalized coords to screen coords
      const ax = a.x * canvas.width;
      const ay = a.y * canvas.height;
      const aw = a.width * canvas.width;
      const ah = a.height * canvas.height;

      ctx.strokeRect(ax, ay, aw, ah);
      // Label background
      ctx.fillStyle = lt.color;
      const labelText = a.label || lt.label;
      ctx.font = 'bold 11px system-ui';
      const textW = ctx.measureText(labelText).width + 8;
      ctx.fillRect(ax, ay - 18, textW, 18);
      ctx.fillStyle = '#ffffff';
      ctx.fillText(labelText, ax + 4, ay - 5);
    });

    // Draw current drawing rect
    if (drawing) {
      ctx.strokeStyle = '#A36A3A';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(drawing.startX, drawing.startY, drawing.endX - drawing.startX, drawing.endY - drawing.startY);
    }
  }, [imgEl, zoom, pan, rotation, annotations, drawing]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);
  useEffect(() => {
    const handleResize = () => drawCanvas();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawCanvas]);

  // Mouse handlers
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.max(0.2, Math.min(5, z + delta)));
  };

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (mode === 'annotate') {
      setDrawing({ startX: x, startY: y, endX: x, endY: y });
    } else {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (mode === 'annotate' && drawing) {
      const rect = canvasRef.current.getBoundingClientRect();
      setDrawing(d => ({ ...d, endX: e.clientX - rect.left, endY: e.clientY - rect.top }));
    } else if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  };

  const handleMouseUp = () => {
    if (mode === 'annotate' && drawing) {
      const canvas = canvasRef.current;
      const w = Math.abs(drawing.endX - drawing.startX);
      const h = Math.abs(drawing.endY - drawing.startY);
      if (w > 10 && h > 10) {
        // Normalize coords to 0-1 range
        setPendingRect({
          x: Math.min(drawing.startX, drawing.endX) / canvas.width,
          y: Math.min(drawing.startY, drawing.endY) / canvas.height,
          width: w / canvas.width,
          height: h / canvas.height,
        });
        setShowLabelInput(true);
      }
      setDrawing(null);
    }
    setIsPanning(false);
  };

  const saveAnnotation = async () => {
    if (!pendingRect) return;
    try {
      const res = await api.post('/admin/annotations', {
        asset_id: asset.id,
        ...pendingRect,
        label: annotationLabel || LABEL_TYPES.find(t => t.value === annotationLabelType)?.label || 'Note',
        label_type: annotationLabelType,
      });
      setAnnotations(prev => [...prev, res.data]);
      toast.success('Annotation saved');
    } catch { toast.error('Failed to save'); }
    setShowLabelInput(false);
    setPendingRect(null);
    setAnnotationLabel('');
  };

  const deleteAnnotation = async (id) => {
    try {
      await api.delete(`/admin/annotations/${id}`);
      setAnnotations(prev => prev.filter(a => a.id !== id));
      toast.success('Annotation deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="image-inspector">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-black/80 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-white/70 text-xs">{asset.slot_key}</span>
          {position && <Badge variant="outline" className="text-[9px] text-white/60 border-white/20" data-testid="inspector-position">{position}</Badge>}
          <Badge variant="outline" className="text-[9px] text-white/60 border-white/20">{Math.round(zoom * 100)}%</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-white/70 hover:text-white text-xs gap-1" onClick={() => setZoom(z => Math.min(5, z + 0.25))}><ZoomIn className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-white/70 hover:text-white text-xs gap-1" onClick={() => setZoom(z => Math.max(0.2, z - 0.25))}><ZoomOut className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-white/70 hover:text-white text-xs gap-1" onClick={() => setZoom(1)}><Maximize2 className="h-3.5 w-3.5" /> Fit</Button>
          <div className="w-px h-5 bg-white/20 mx-1" />
          <Button variant="ghost" size="sm" className="h-7 text-white/70 hover:text-white text-xs" onClick={() => onRotate && onRotate((rotation - 90 + 360) % 360)}><RotateCcw className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 text-white/70 hover:text-white text-xs" onClick={() => onRotate && onRotate((rotation + 90) % 360)}><RotateCw className="h-3.5 w-3.5" /></Button>
          <div className="w-px h-5 bg-white/20 mx-1" />
          <Button variant="ghost" size="sm" className={`h-7 text-xs gap-1 ${asset.is_certificate_image ? 'text-yellow-400' : 'text-white/40'}`} onClick={() => onToggleCert && onToggleCert(!asset.is_certificate_image)}><Star className="h-3.5 w-3.5" /> Cert</Button>
          <div className="w-px h-5 bg-white/20 mx-1" />
          <Button variant={mode === 'annotate' ? 'secondary' : 'ghost'} size="sm" className={`h-7 text-xs gap-1 ${mode === 'annotate' ? 'text-amber-300 bg-amber-900/40' : 'text-white/70 hover:text-white'}`} onClick={() => setMode(m => m === 'annotate' ? 'view' : 'annotate')} data-testid="annotate-toggle">
            <Square className="h-3.5 w-3.5" /> Annotate
          </Button>
          <Button variant="ghost" size="sm" className="h-7 text-white/70 hover:text-white ml-2" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Canvas area */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden cursor-crosshair" style={{ cursor: mode === 'annotate' ? 'crosshair' : isPanning ? 'grabbing' : 'grab' }}>
        <canvas
          ref={canvasRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className="absolute inset-0"
        />
        {!imgLoaded && !imgError && <div className="absolute inset-0 flex items-center justify-center"><span className="text-white/50 text-sm">Loading image...</span></div>}
        {imgError && (
          <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="inspector-image-error">
            <div className="max-w-lg bg-black/60 backdrop-blur-sm border border-red-500/40 rounded p-4 text-center space-y-2">
              <p className="text-red-300 text-sm">Image failed to load after retry.</p>
              {diagnosticKey && (
                <>
                  <p className="text-white/60 text-[11px]">
                    <span className="text-white/40">storage_key:</span>{' '}
                    <span className="font-mono break-all">{diagnosticKey}</span>
                  </p>
                  <button
                    type="button"
                    className="text-[11px] px-2 py-1 rounded border border-white/20 text-white/80 hover:bg-white/10"
                    data-testid="inspector-image-error-copy"
                    onClick={() => {
                      try {
                        navigator.clipboard.writeText(String(diagnosticKey));
                        toast.success('Copied diagnostic key');
                      } catch {
                        toast.error('Copy failed — select and copy manually');
                      }
                    }}
                  >
                    Copy diagnostic
                  </button>
                </>
              )}
              <p className="text-white/40 text-[11px]">Close and reopen the item, or send the diagnostic above to support.</p>
            </div>
          </div>
        )}
        {onPrev && (
          <button onClick={onPrev} className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors" title="Previous image (←)" data-testid="inspector-prev-btn">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        {onNext && (
          <button onClick={onNext} className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors" title="Next image (→)" data-testid="inspector-next-btn">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Annotation label input popup */}
      {showLabelInput && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl p-4 z-50 w-80" onClick={e => e.stopPropagation()}>
          <h3 className="text-sm font-semibold text-ink mb-3 flex items-center gap-1.5"><Type className="h-4 w-4" /> Add Annotation</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Type</label>
              <Select value={annotationLabelType} onValueChange={setAnnotationLabelType}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LABEL_TYPES.map(lt => {
                    const Icon = lt.icon;
                    return <SelectItem key={lt.value} value={lt.value}><div className="flex items-center gap-1.5"><Icon className="h-3 w-3" style={{color: lt.color}} />{lt.label}</div></SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Label</label>
              <Input value={annotationLabel} onChange={e => setAnnotationLabel(e.target.value)} className="mt-1 h-8 text-xs" placeholder="e.g. Stitching pattern inconsistent" autoFocus data-testid="annotation-label-input" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="flex-1 text-xs" onClick={saveAnnotation} data-testid="save-annotation-btn">Save Annotation</Button>
              <Button size="sm" variant="outline" className="text-xs" onClick={() => { setShowLabelInput(false); setPendingRect(null); }}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Annotation list sidebar */}
      {annotations.length > 0 && (
        <div className="absolute right-0 top-12 bottom-0 w-56 bg-black/70 border-l border-white/10 overflow-y-auto p-2">
          <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2 px-1">Annotations ({annotations.length})</p>
          {annotations.map(a => {
            const lt = LABEL_TYPES.find(t => t.value === a.label_type) || LABEL_TYPES[3];
            return (
              <div key={a.id} className="p-2 rounded-lg mb-1.5" style={{ background: lt.color + '20', border: `1px solid ${lt.color}40` }}>
                <div className="flex items-center justify-between">
                  <Badge className="text-[9px]" style={{ background: lt.color, color: '#fff' }}>{a.label_type}</Badge>
                  <button onClick={() => deleteAnnotation(a.id)} className="text-white/40 hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
                </div>
                <p className="text-white text-xs mt-1">{a.label}</p>
                <p className="text-white/40 text-[9px] mt-0.5">{a.author_email}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
